import { ChangeDetectionStrategy, Component, computed, inject, SecurityContext, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { COLUMN_SEPARATORS, ENVELOPE_CURVE_CONSTANTS } from '../../../../../../constants/envelope-curve.constants';
import { ConfirmationHelperService } from '../../../../../../services/confirmation-helper.service';
import { ExcelParserService } from '../../../../../../services/excel-parser.service';
import { ToastService } from '../../../../../../services/toast.service';
import { Envelope, EnvelopePoint } from '../../../../../../types/graph-dialog.interface';
import { formatClipboardNumericValue } from '../../../../../../utils/grid/numeric-value-formatter';
import { CesDialogFooterComponent } from '../../../../../ui/dialog-footer/dialog-footer.component';

interface PointForm {
  x: number | null;
  y: number | null;
}

interface PointFormControls {
  x: FormControl<number | null>;
  y: FormControl<number | null>;
}

type PointFormGroup = FormGroup<PointFormControls>;

interface EnvelopeDialogData {
  envelope?: Envelope;
}

@Component({
  selector: 'app-envelope-curves-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, TranslocoModule, InputTextModule, CesDialogFooterComponent],
  host: {
    '(document:keydown.escape)': 'handleEscape($event)',
  },
  templateUrl: './envelope-curves-dialog.component.html',
})
export class EnvelopeCurvesDialogComponent {
  private readonly ref = inject(DynamicDialogRef);
  private readonly config = inject<DynamicDialogConfig<EnvelopeDialogData>>(DynamicDialogConfig);
  private readonly fb = inject(FormBuilder);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly confirmationHelper = inject(ConfirmationHelperService);
  private readonly excelParser = inject(ExcelParserService);
  private readonly translate = inject(TranslocoService);
  private readonly toastService = inject(ToastService);

  protected readonly MIN_POINTS = ENVELOPE_CURVE_CONSTANTS.MIN_POINTS;
  protected readonly MIN_VALUE = ENVELOPE_CURVE_CONSTANTS.MIN_VALUE;
  protected readonly MAX_VALUE = ENVELOPE_CURVE_CONSTANTS.MAX_VALUE;
  protected readonly NAME_MAX_LENGTH = ENVELOPE_CURVE_CONSTANTS.NAME_MAX_LENGTH;

  readonly form = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(ENVELOPE_CURVE_CONSTANTS.NAME_MAX_LENGTH)]],
    points: this.fb.array([this.createPoint(), this.createPoint()]),
  });

  private readonly formValue = signal(this.form.value);

  readonly validPointsCount = computed(() => {
    const value = this.formValue();
    const points = value.points ?? [];
    return points.filter(p => p.x !== null && p.x !== undefined && p.y !== null && p.y !== undefined).length;
  });

  readonly isValid = computed(() => {
    const value = this.formValue();
    const nameValid = !!value.name && value.name.length > 0 && value.name.length <= ENVELOPE_CURVE_CONSTANTS.NAME_MAX_LENGTH;
    return nameValid && this.validPointsCount() >= ENVELOPE_CURVE_CONSTANTS.MIN_POINTS;
  });

  readonly pointsArray = computed(() => this.form.controls.points);
  readonly nameControl = computed(() => this.form.controls.name);

  constructor() {
    this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.formValue.set(this.form.value);
      this.autoAddPointRow();
    });

    if (this.config.data?.envelope) {
      for (let index = 0; index < this.config.data.envelope.points.length - 2; index++) {
        this.pointsArray().push(this.createPoint());
      }
      this.form.patchValue({
        name: this.config.data.envelope.name,
        points: this.config.data.envelope.points.map((point: EnvelopePoint) => {
          return { x: point.x, y: point.y };
        }),
      });
    } else {
    }
  }

  private createPoint(): PointFormGroup {
    return new FormGroup<PointFormControls>({
      x: new FormControl<number | null>(null, [
        Validators.required,
        Validators.min(ENVELOPE_CURVE_CONSTANTS.MIN_VALUE),
        Validators.max(ENVELOPE_CURVE_CONSTANTS.MAX_VALUE),
      ]),
      y: new FormControl<number | null>(null, [
        Validators.required,
        Validators.min(ENVELOPE_CURVE_CONSTANTS.MIN_VALUE),
        Validators.max(ENVELOPE_CURVE_CONSTANTS.MAX_VALUE),
      ]),
    });
  }

  private autoAddPointRow(): void {
    const pointsArray = this.pointsArray();
    if (pointsArray.length === 0) return;
    const lastPoint = pointsArray.at(pointsArray.length - 1).value;

    if (lastPoint.x !== null && lastPoint.y !== null) {
      pointsArray.push(this.createPoint());
    }
  }

  onPaste(event: ClipboardEvent, startIndex: number, startField: 'x' | 'y'): void {
    event.preventDefault();

    const pastedText = event.clipboardData?.getData('text');
    if (!pastedText?.trim()) return;

    try {
      const points = this.excelParser.parseExcelData(pastedText);

      if (points.length === 0) {
        return;
      }

      const pointsArray = this.pointsArray();
      let currentIndex = startField === 'y' ? startIndex + 1 : startIndex;
      const neededRows = currentIndex + points.length;

      while (pointsArray.length < neededRows) {
        pointsArray.push(this.createPoint());
      }

      points.forEach((point, idx) => {
        const targetIndex = currentIndex + idx;
        if (targetIndex < pointsArray.length) {
          pointsArray.at(targetIndex).patchValue({ x: point.x, y: point.y });
        }
      });

      const lastPoint = pointsArray.at(pointsArray.length - 1).value;
      if (lastPoint.x !== null || lastPoint.y !== null) {
        pointsArray.push(this.createPoint());
      }
    } catch (error) {
      console.error('Error parsing pasted data:', error);
    }
  }

  insertPointAt(index: number): void {
    this.pointsArray().insert(index, this.createPoint());
  }

  isEmptyRow(index: number): boolean {
    const arr = this.pointsArray();
    if (index >= arr.length) return true;
    const point = arr.at(index)?.value;
    return !point || (point.x === null && point.y === null);
  }

  isLastRow(index: number): boolean {
    return index === this.pointsArray().length - 1;
  }

  removeOrResetPoint(index: number): void {
    const pointsArray = this.pointsArray();
    if (pointsArray.length <= ENVELOPE_CURVE_CONSTANTS.MIN_POINTS) {
      pointsArray.at(index).patchValue({ x: null, y: null });
      pointsArray.at(index).markAsUntouched();
    } else {
      pointsArray.removeAt(index);
    }
  }

  /** Copies every filled point as a tab separated block Excel and `onPaste` both understand. */
  async copyPoints(): Promise<void> {
    const filledPoints = this.pointsArray()
      .controls.map(control => control.value as PointForm)
      .filter((point): point is { x: number; y: number } => point.x !== null && point.y !== null);

    if (filledPoints.length === 0) {
      this.toastService.warning('common.toasts.noValuesToCopy');
      return;
    }

    const lines = [
      ['X', 'Y'].join(COLUMN_SEPARATORS.TAB),
      ...filledPoints.map(point =>
        [formatClipboardNumericValue(point.x), formatClipboardNumericValue(point.y)].join(COLUMN_SEPARATORS.TAB),
      ),
    ];

    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      this.toastService.success('common.toasts.valuesCopied', undefined, undefined, undefined, { count: filledPoints.length });
    } catch {
      this.toastService.error('common.toasts.copyFailed');
    }
  }

  async resetPoints(): Promise<void> {
    const pointsArray = this.pointsArray();
    const shouldConfirm = pointsArray.length > ENVELOPE_CURVE_CONSTANTS.MIN_POINTS;

    if (shouldConfirm) {
      const confirmed = await this.confirmationHelper.ask(
        'graphs.envelope.confirmReset',
        'common.dialogs.confirm',
        undefined,
        'pi pi-exclamation-triangle',
        'common.buttons.reset',
      );
      if (!confirmed) return;
    }

    this.performReset();
  }

  private performReset(): void {
    const pointsArray = this.pointsArray();
    pointsArray.clear();
    Array.from({ length: ENVELOPE_CURVE_CONSTANTS.MIN_POINTS }).forEach(() => {
      pointsArray.push(this.createPoint());
    });
  }

  protected getAriaLabelX(index: number): string {
    return this.translate.translate('graphs.envelope.valueXOfPoint', { point: index + 1 });
  }

  protected getAriaLabelY(index: number): string {
    return this.translate.translate('graphs.envelope.valueYOfPoint', { point: index + 1 });
  }

  protected getPointActionAriaLabel(index: number): string {
    const key =
      this.pointsArray().length <= ENVELOPE_CURVE_CONSTANTS.MIN_POINTS ? 'graphs.envelope.resetPoint' : 'graphs.envelope.deletePoint';
    return this.translate.translate(key, { point: index + 1 });
  }

  handleEscape(event: KeyboardEvent): void {
    event.preventDefault();
    this.cancel();
  }

  cancel(): void {
    this.ref.close();
  }

  save(): void {
    if (!this.isValid()) return;

    try {
      const rawName = this.nameControl().value ?? '';
      const sanitizedName = this.sanitizer.sanitize(SecurityContext.NONE, rawName.trim()) ?? '';

      if (!sanitizedName) {
        this.toastService.error('common.toasts.saveFailed');
        return;
      }

      const pointsArray = this.pointsArray();
      const validPoints = pointsArray.controls
        .map(control => control.value as PointForm)
        .filter((p): p is { x: number; y: number } => p.x !== null && p.y !== null);

      if (validPoints.length < ENVELOPE_CURVE_CONSTANTS.MIN_POINTS) {
        this.toastService.error('common.toasts.saveFailed');
        return;
      }

      this.ref.close({
        name: sanitizedName,
        points: validPoints,
        metadata: undefined,
        id: -1,
      } satisfies Envelope);
    } catch {
      this.toastService.error('common.toasts.operationFailed');
    }
  }
}
